import { Component, ContentType, Field } from 'contensis-core-api';
import { Entry } from 'contensis-management-api';
import { ContentTypeResolver } from './ContentTypeResolver';
import { ContentTypeAssertions } from './ContentTypeAssertions';
import { ContentTypeFieldHelper } from './ContentTypeFieldHelper';
import { dedupe } from '~/util/array';
import { getSys } from '~/util/EntryHelper';
import { ModelDependencies } from '~/models';
import { FormContentType } from '~/models/FormContentTypeManagement';

export class ContentTypeDependencyResolver {
  constructor(private resolve: ContentTypeResolver) {}

  /**
   * Loop through content type or component fields one time to look for all possible
   * dependency types in each field
   * @param resource Content type or Component
   * @returns list of dependency ids under each key of { contentTypeIds, componentIds, defaults, nodes }
   */
  resolveIds = (resource: ContentType | Component | FormContentType) => {
    if (!resource)
      return {
        contentTypes: [],
        components: [],
        defaults: [],
        nodes: [],
        tagGroups: [],
      };
    const { resolve } = this;
    const fieldHelper = ContentTypeFieldHelper;
    const { assert } = fieldHelper;
    const assertResource = ContentTypeAssertions;
    const { fields = [] } = resource;
    const isComponent = assertResource.isComponent(resource);

    const linkedEntryFields: Field[] = []; // contains contentTypeIds componentIds or entryIds
    const canvasFields: Field[] = []; // contains contentTypeIds componentIds
    const resourcePickerFields: Field[] = []; // contains contentTypeIds
    const tagPickerFields: Field[] = []; // contains tagGroupIds
    const componentFields: Field[] = []; // contains contentTypeIds componentIds or entryIds
    const composerFields: Field[] = []; // contains contentTypeIds componentIds or entryIds
    const composerCanvasFields: Field[] = []; // contains contentTypeIds componentIds
    const composerComponentFields: Field[] = []; // contains contentTypeIds componentIds or entryIds
    const composerResourcePickerFields: Field[] = []; // contains contentTypeIds
    const composerTagPickerFields: Field[] = []; // contains tagGroupIds

    const imageFields: Field[] = []; // contains entryIds
    const assetFields: Field[] = []; // contains entryIds

    // Loop through content type / component fields and resolve field types that can contain dependencies
    for (const field of fields) {
      // Resolve entry link field
      if (assert.IsEntry(field)) linkedEntryFields.push(field);
      // Resolve asset link fields that may contain a default asset
      else if (assert.IsImage(field)) imageFields.push(field);
      else if (assert.IsAsset(field)) assetFields.push(field);
      // Resolve canvas field
      else if (assert.IsCanvas(field)) canvasFields.push(field);
      // Resolve component field
      else if (assert.IsComponent(field)) componentFields.push(field);
      // Resolve composer field
      else if (assert.IsComposer(field)) {
        composerFields.push(field);
        for (const cf of fieldHelper.GetComposerFields(field)) {
          // Resolve component fields inside composer field
          if (assert.IsComponent(cf)) composerComponentFields.push(cf);
          // Resolve canvas field inside composer field
          else if (assert.IsCanvas(cf)) composerCanvasFields.push(cf);
          // Resolve resource picker field
          else if (assert.IsResourcePicker(cf))
            composerResourcePickerFields.push(cf);
          // Resolve tag picker field
          else if (assert.IsTagPicker(cf)) composerTagPickerFields.push(cf);
        }
        // Resolve resource picker field
      } else if (assert.IsResourcePicker(field))
        resourcePickerFields.push(field);
      // Resolve tag picker field
      else if (assert.IsTagPicker(field)) tagPickerFields.push(field);
    }

    /**
     * Resolve Dependent contentTypeIds
     */

    // Get allowed content types in
    //   - linked entry fields inside the main content type fields
    //   - canvas fields
    //   - resource picker fields
    const linkedContentTypes = [
      ...linkedEntryFields,
      ...canvasFields,
      ...resourcePickerFields,
      ...composerCanvasFields,
      ...composerResourcePickerFields,
    ].map((f) => fieldHelper.GetAllowedContentTypes(f));

    // Get allowed content types in nested component fields
    // (component fields resolved in a content type does not contain the fields array any more)
    const componentLinkedContentTypes = (
      isComponent ? componentFields : []
    ).map(
      (cf) =>
        (cf as Field & { fields: Field[] }).fields?.map((icf) =>
          fieldHelper.GetAllowedContentTypes(icf)
        ) || []
    );

    // Get allowed content types in composer fields
    const composerLinkedContentTypes = composerFields.map((f) =>
      fieldHelper
        .GetComposerFields(f)
        .map((icf) => fieldHelper.GetAllowedContentTypes(icf))
    );

    const foundCtIds = dedupe([
      ...linkedContentTypes.flat(),
      ...componentLinkedContentTypes.flat(2),
      ...composerLinkedContentTypes.flat(2),
    ]);

    // TODO: remove the need to normalise ids so we can make this method static
    // Normalise list of contentTypeIds as they may not be in the correct case
    const contentTypeIds = foundCtIds.map((id) => resolve.contentTypeId(id));

    /**
     * Resolve Dependent componentIds
     */

    // Get the component id from resolved
    //   - component fields
    //   - composer component fields
    const resolvedComponentIds = [
      ...componentFields,
      ...composerComponentFields,
    ].map((c) => c.dataFormat?.replace('component.', '') || '');

    // Get any component id from resolved canvas fields
    const canvasComponentIds = [...canvasFields, ...composerCanvasFields].map(
      (canvasField) =>
        (canvasField.validations as any)?.allowedTypes?.types.find(
          ({ type }: { type: string }) => type === '_component'
        )?.components?.allowed || []
    ) as string[];

    const foundCmpIds = dedupe(
      [
        ...resolvedComponentIds,
        ...canvasComponentIds.flat(),
        // lowercase the componentIds to match those found in content type fields
        // so we can dedupe them
      ].map((id) => id.toLowerCase())
    );

    // TODO: remove the need to normalise ids so we can make this method static
    // Normalise list of componentIds as they will not be in the correct case
    const componentIds = foundCmpIds.map((id) => resolve.componentId(id));

    /**
     * Resolve Dependent Default entryIds
     */

    // Get any defaults in linked entry fields inside the main content type fields
    const linkedEntryDefaults = linkedEntryFields
      .map((f) => fieldHelper.GetDefaultEntry(f))
      .filter((e) => !!e);

    const linkedAssetDefaults = [...assetFields, ...imageFields]
      .map((f) => fieldHelper.GetDefaultEntry(f))
      .filter((e) => !!e);

    // Get default entries in component fields
    const componentLinkedEntryDefaults = isComponent
      ? [...componentFields, ...composerComponentFields].map((cf) =>
          (cf as Field & { fields: Field[] }).fields
            .map(
              (icf) =>
                (assert.IsEntry(icf) ||
                  assert.IsAsset(icf) ||
                  assert.IsImage(icf)) &&
                fieldHelper.GetDefaultEntry(icf)
            )
            .filter((e) => !!e)
        )
      : [];

    // Get default entries in composer fields
    const composerLinkedEntryDefaults = composerFields.map((f) =>
      fieldHelper
        .GetComposerFields(f)
        .map(
          (icf) =>
            (assert.IsEntry(icf) ||
              assert.IsAsset(icf) ||
              assert.IsImage(icf)) &&
            fieldHelper.GetDefaultEntry(icf)
        )
        .filter((e) => !!e)
    );

    const foundEntryDefaults: Entry[] = dedupe([
      ...linkedEntryDefaults.flat(),
      ...linkedAssetDefaults.flat(),
      ...componentLinkedEntryDefaults.flat(2),
      ...composerLinkedEntryDefaults.flat(2),
    ]);

    /**
     * Resolve dependent assets or images
     */

    const defaults = foundEntryDefaults.map<[string, string]>((e) => [
      getSys(e).id || '',
      getSys(e).contentTypeId || getSys(e).dataFormat || '',
    ]);

    /**
     * Resolve dependent nodeIds
     */
    const nodes: [string][] = [];
    if (assertResource.isEntryContentType(resource)) {
      if (resource.defaultParentNodeId)
        nodes.push([resource.defaultParentNodeId]);
    }
    if (assertResource.isFormContentType(resource)) {
      for (const rule of resource.properties?.confirmationRules || []) {
        if ('link' in rule.return && rule.return.link.sys?.node?.id)
          nodes.push([rule.return.link.sys.node.id]);
      }
    }

    /**
     * Resolve dependent tagGroupIds
     */
    const tagGroupIds = dedupe(
      [...tagPickerFields, ...composerTagPickerFields].map((g) =>
        resolve.tagGroupId(g.dataFormat?.toLowerCase())
      )
    );

    return {
      contentTypes: contentTypeIds,
      components: componentIds,
      defaults,
      nodes,
      tagGroups: tagGroupIds,
    } as ModelDependencies['dependencies'];
  };
}
